// =============================================================================
// ACT SCIENCE TEST BANK INDEX
// Combines all Science test forms for Testing Mode
// =============================================================================

import { 
  formId as FORM_ID_25MC1,
  questions as questions_25MC1,
  scoringTable as SCORING_TABLE_25MC1,
  passages as PASSAGES_25MC1,
  categories as CATEGORIES_25MC1,
  totalQuestions as TOTAL_25MC1,
  scoredQuestions as SCORED_25MC1,
  fieldTestPositions as FIELD_TEST_25MC1,
  timeLimit as TIME_25MC1
} from './testBank-science-25MC1.js';

import { 
  formId as FORM_ID_J08,
  questions as questions_J08,
  scoringTable as SCORING_TABLE_J08,
  passages as PASSAGES_J08,
  categories as CATEGORIES_J08,
  totalQuestions as TOTAL_J08,
  scoredQuestions as SCORED_J08,
  fieldTestPositions as FIELD_TEST_J08,
  timeLimit as TIME_J08
} from './testBank-science-J08.js';

// -----------------------------------------------------------------------------
// SUBJECT METADATA
// -----------------------------------------------------------------------------
export const subjectInfo = {
  id: 'science',
  name: 'Science',
  icon: '🔬',
  color: 'green',
  totalTime: 40,
  description: 'Data interpretation and analysis'
};

// -----------------------------------------------------------------------------
// CATEGORY DEFINITIONS
// -----------------------------------------------------------------------------
export const categoryDefinitions = {
  IOD: { name: 'Interpretation of Data', short: 'IOD', description: 'Manipulate and analyze data in tables, graphs, and diagrams' },
  SIN: { name: 'Scientific Investigation', short: 'SIN', description: 'Understand experimental tools, procedures, and design' },
  EMI: { name: 'Evaluation of Models & Inferences', short: 'EMI', description: 'Judge validity of information and formulate conclusions' }
};

// -----------------------------------------------------------------------------
// ALL TEST FORMS
// -----------------------------------------------------------------------------
export const testFormsData = {
  '25MC1': {
    metadata: {
      formId: '25MC1',
      subject: 'science',
      source: 'Preparing for the ACT 2025-2026',
      testType: 'Practice Test',
      format: 'Enhanced',
      totalQuestions: TOTAL_25MC1,
      scoredQuestions: SCORED_25MC1,
      fieldTestPositions: FIELD_TEST_25MC1,
      timeMinutes: TIME_25MC1
    },
    questions: questions_25MC1,
    scoringTable: SCORING_TABLE_25MC1,
    passages: PASSAGES_25MC1,
    categories: CATEGORIES_25MC1
  },
  'J08': {
    metadata: {
      formId: 'J08',
      subject: 'science',
      source: 'ACT October 2025 Release',
      testType: 'Released Test',
      format: 'Scored Only',
      totalQuestions: TOTAL_J08,
      scoredQuestions: SCORED_J08,
      fieldTestPositions: FIELD_TEST_J08,
      timeMinutes: TIME_J08
    },
    questions: questions_J08,
    scoringTable: SCORING_TABLE_J08,
    passages: PASSAGES_J08,
    categories: CATEGORIES_J08
  }
};

// -----------------------------------------------------------------------------
// TEST FORMS LIST (for dashboard display)
// -----------------------------------------------------------------------------
export const testFormsList = [
  {
    id: '25MC1',
    subject: 'science',
    name: 'Science Form 25MC1',
    format: 'Enhanced',
    source: 'Preparing for the ACT 2025-2026',
    questions: 40,
    scoredQuestions: 34,
    duration: 40,
    fieldTestPositions: [29, 30, 31, 32, 33, 34],
    available: true,
    hasRealQuestions: true
  },
  {
    id: 'J08',
    subject: 'science',
    name: 'Science Form J08',
    format: 'Scored Only',
    source: 'ACT October 2025 Release',
    questions: 34,
    scoredQuestions: 34,
    duration: 35,
    fieldTestPositions: [],
    available: true,
    hasRealQuestions: true
  }
];

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Get questions for a specific test form
 */
export function getTestQuestions(formId) {
  const form = testFormsData[formId];
  if (form) {
    return form.questions;
  }
  return [];
}

/**
 * Get only scored questions (excluding field test items)
 */
export function getScoredQuestions(formId) {
  const questions = getTestQuestions(formId);
  return questions.filter(q => q.isScored !== false);
}

/**
 * Calculate scaled score for a test form
 */
export function calculateTestScore(formId, rawScore) {
  const form = testFormsData[formId];
  if (form && form.scoringTable) {
    return form.scoringTable[rawScore] || 1;
  }
  // Default scoring for 34 scored questions
  return Math.round(1 + (rawScore / 34) * 35);
}

/**
 * Check if a form has real questions loaded
 */
export function hasRealQuestions(formId) {
  return !!testFormsData[formId]?.questions?.length;
}

/**
 * Get form metadata
 */
export function getFormMetadata(formId) {
  return testFormsData[formId]?.metadata || null;
}

/**
 * Get passages for a form
 */
export function getPassages(formId) {
  return testFormsData[formId]?.passages || [];
}

// -----------------------------------------------------------------------------
// STATS
// -----------------------------------------------------------------------------
export const testBankStats = {
  subject: 'science',
  totalForms: testFormsList.length,
  formsWithQuestions: Object.keys(testFormsData).length,
  totalQuestions: Object.values(testFormsData).reduce((sum, form) => sum + (form.questions?.length || 0), 0)
};

console.log(`Science Test Bank Loaded: ${testBankStats.formsWithQuestions} forms with ${testBankStats.totalQuestions} questions`);
